import React, { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useAppStore } from "@/hooks/useAppStore";
import { useTranslation } from "@/lib/translate";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { createConsistentUsernameFromUUID } from "@/lib/userUtils";

//
interface TextInputProps {
  isAdmin?: boolean;
  userList?: Array<{ username: string, displayUsername?: string }>;
}

export const TextInput: React.FC<TextInputProps> = ({
  isAdmin,
  userList
}) => {
  const [message, setMessage] = useState("");
  const [userName, setUserName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<HTMLButtonElement>(null);
  const sendMessage = useAppStore((state) => state.sendMessage);
  const isConnected = useAppStore((state) => state.isConnected);
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);

  const t = useTranslation(preferredLanguage);

  // Focus input when component mounts
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = () => {
    if (message.trim() && isConnected) {
      if (isAdmin && !userName) {
        alert("Select a user to post as");
        selectRef.current?.focus();
        return;
      }

      sendMessage(message.trim(), isAdmin ? userName : undefined);
      setMessage("");
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex items-center space-x-3 w-full">
      <Input
        ref={inputRef}
        type="text"
        placeholder={t("Type your message...")}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={!isConnected}
        className="flex-1 border-1 focus:border-chotto-mic-green dark:focus:border-chotto-mic-green rounded-lg px-4 py-2 text-sm font-sans"
      />
      {isAdmin && userList && userList.length > 0 && <div className="">
        <Select value={userName} onValueChange={(value) => setUserName(value)}>
          <SelectTrigger ref={selectRef} className="w-32">
            <SelectValue placeholder="Post as" />
          </SelectTrigger>
          <SelectContent>
            {userList.map(user => {
              const displayName = user.displayUsername || createConsistentUsernameFromUUID(user.username);
              return (
                <SelectItem key={user.username} value={user.username}>
                  {displayName}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>}
      <Button
        onClick={handleSend}
        disabled={!message.trim() || !isConnected}
        className="h-10 w-10 rounded-full bg-chotto-mic-green disabled:bg-neutral-300 dark:disabled:bg-neutral-700 p-0 flex items-center justify-center"
      >
        <ArrowRight className="h-5 w-5 text-white" />
      </Button>
    </div>
  );
};
